const PREFIX = 'rconf:meeting-snapshot:v1:';
const TTL = 5 * 60 * 1000;
const LIMIT = 32;
const revisions = new Map();
const refreshes = new Map();
const memory = new Map();
const pending = new Map();
let generation = 0;

export function resourceOwner(runtime) {
  const state = runtime.getState?.();
  if (state?.status !== 'authenticated' || !state.user?.id) return '';
  const u = state.user, f = u.features_json || u.features || {}, i = state.impersonation || {};
  return JSON.stringify([String(u.id), String(u.company_id || ''), [...(u.company_ids || [])].sort(), String(u.role || ''),
    Boolean(i.active), String(i.impersonatorUserId || ''),
    ['expert_mode_enabled','library_enabled','digest_enabled','neo_ai_enabled','external_recordings_enabled'].map(key => f[key])]);
}

export function retainsResource(endpoint, options = {}) {
  return (!options.method || String(options.method).toUpperCase() === 'GET') && !options.body &&
    (/^\/api\/(?:meetings|meeting-recordings|team-recordings|external-recordings|shared-meetings|team-recording-types|company-participants|prompts|dashboard-templates|processing-model-options|meetings\/bbb-history|meeting-preparation\/templates|neo-ai\/agents)(?:\?|$)/.test(endpoint) || /^\/api\/calendar-integrations\/google\/events(?:\?|$)/.test(endpoint)) && new URLSearchParams(endpoint.split('?')[1] || '').get('sync') !== '1';
}

function persistent(endpoint) { return /^\/api\/meetings(?:\?|$)/.test(endpoint); }
function key(owner, endpoint) { return PREFIX + encodeURIComponent(owner) + ':' + endpoint; }
function storage() { try { return globalThis.sessionStorage; } catch { return null; } }
function forget(entryKey) { memory.delete(entryKey); try { storage()?.removeItem(entryKey); } catch {} }
function valid(entry, owner, endpoint) {
  return entry && entry.owner === owner && entry.endpoint === endpoint && Number.isFinite(entry.at) &&
    Date.now() >= entry.at && Date.now() - entry.at <= TTL && entry.data != null;
}

// Persist only display metadata. Access tokens, links, passwords, transcripts,
// prompts and meeting content never enter the browser snapshot.
const FIELDS = new Set(['id','_id','_key','uuid','meeting_id','recording_id','name','title','display_title','type','status',
  'startsAt','endsAt','schedule_mode','isInvitation','historyImported','readOnly','startAt','start_at','scheduled_at','endAt','end_at','finished_at','createdAt','created_at','updatedAt','updated_at',
  'date','duration','durationLabel','durationMinutes','duration_minutes','length','isOwner','is_owner','isInvited','is_invited',
  'canStart','can_start','canEdit','can_edit','canDelete','can_delete','owner_id','ownerId','ownerName','owner_name',
  'participantCount','participant_count','recordingCount','recordingsCount','recording_count','summaryCount','summary_count',
  'hasRecording','has_recording','hasSummaries','has_summaries','isPeriodic','is_periodic','timezone','periodicity']);
function displayRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  return Object.fromEntries(Object.entries(row).filter(([k,v]) => FIELDS.has(k) &&
    (typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || v === null)));
}
function displayData(data) {
  if (Array.isArray(data)) return data.slice(0,200).map(displayRow).filter(Boolean);
  if (!data || typeof data !== 'object') return null;
  const copy = {};
  for (const [k,v] of Object.entries(data)) {
    if (['items','meetings','rows','results','data'].includes(k) && Array.isArray(v)) copy[k] = v.slice(0,200).map(displayRow).filter(Boolean);
    else if (['total','count','page','page_size','per_page','pages','total_pages','current_page','has_next','has_previous'].includes(k) &&
      ['number','boolean'].includes(typeof v)) copy[k] = v;
  }
  return Object.keys(copy).some(k => Array.isArray(copy[k])) ? copy : null;
}

export function readResourceSnapshot(owner, endpoint) {
  if (!owner || !retainsResource(endpoint)) return null;
  const entryKey = key(owner,endpoint);
  let entry = memory.get(entryKey);
  if (entry && !valid(entry,owner,endpoint)) { forget(entryKey); entry = null; }
  if (entry) return entry;
  if (!persistent(endpoint)) return null;
  try {
    entry = JSON.parse(storage()?.getItem(entryKey) || 'null');
    if (!valid(entry,owner,endpoint)) { forget(entryKey); return null; }
    // Sanitize on read too: browser storage is untrusted and may be from an old build.
    entry = {...entry, data:displayData(entry.data), fromStorage:true};
    if (entry.data == null) { forget(entryKey); return null; }
    memory.set(entryKey,entry); return entry;
  } catch { forget(entryKey); return null; }
}

export function writeResourceSnapshot(owner, endpoint, data) {
  if (!owner || !retainsResource(endpoint) || data == null) return;
  const entryKey=key(owner,endpoint), entry={owner,endpoint,data,at:Date.now()};
  memory.delete(entryKey); memory.set(entryKey,entry);
  while(memory.size>LIMIT) forget(memory.keys().next().value);
  if (!persistent(endpoint)) return;
  try {
    const safe=displayData(data);
    if (safe == null) return;
    const body=JSON.stringify({...entry,data:safe});
    if(body.length<=256*1024) storage()?.setItem(entryKey,body);
    const store=storage(); if(!store)return;
    const keys=Object.keys(store).filter(k=>k.startsWith(PREFIX));
    if(keys.length>LIMIT) keys.sort((a,b)=>JSON.parse(store.getItem(a)||'{}').at-JSON.parse(store.getItem(b)||'{}').at).slice(0,keys.length-LIMIT).forEach(k=>store.removeItem(k));
  } catch { /* Memory cache remains available when storage is full or disabled. */ }
}

export function invalidateResourceSnapshot(owner, endpoint) { if(owner) forget(key(owner,endpoint)); }
export function clearAccountSnapshots() {
  generation++; memory.clear(); pending.clear(); revisions.clear(); refreshes.clear();
  try { const s=storage(); if(s)Object.keys(s).filter(k=>k.startsWith(PREFIX)).forEach(k=>s.removeItem(k)); } catch {}
}

export function retainAccountSnapshots(owner) {
  if (!owner) return;
  for (const [k,v] of memory) if(v.owner!==owner) forget(k);
  try { const store=storage(); if(store)Object.keys(store).filter(k=>k.startsWith(PREFIX)).forEach(k=>{
    try { if(JSON.parse(store.getItem(k)||'null')?.owner!==owner) forget(k); } catch { forget(k); }
  }); } catch {}
}

export function requestResource(runtime, endpoint, options = {}, force = false) {
  const owner=resourceOwner(runtime);
  if(!owner || !retainsResource(endpoint,options)) return runtime.request(endpoint,options);
  const entryKey=key(owner,endpoint);
  const fresh=readResourceSnapshot(owner,endpoint);
  if(!force && fresh && !fresh.fromStorage && Date.now()-fresh.at<15000) return Promise.resolve(fresh.data);
  if(pending.has(entryKey)) return pending.get(entryKey);
  const started=generation, revision=revisions.get(owner) || 0;
  const promise=Promise.resolve().then(()=>runtime.request(endpoint,options)).then(data=>{
    if(started===generation && resourceOwner(runtime)===owner) {
      // A mutation may finish while this GET is in flight. Never publish its old rows.
      if(revision !== (revisions.get(owner) || 0)) return requestResource(runtime,endpoint,options,true);
      writeResourceSnapshot(owner,endpoint,data);
    }
    return data;
  }).catch(error=>{
    if([401,403].includes(Number(error?.status))) invalidateOwnerSnapshots(owner);
    throw error;
  }).finally(()=>{if(pending.get(entryKey)===promise)pending.delete(entryKey)});
  pending.set(entryKey,promise);return promise;
}

// Private list rows and catalogs live in memory only. The existing disk allowlist
// continues to persist sanitized meeting display metadata, never recording content.
export function invalidateOwnerSnapshots(owner) {
  if(!owner)return;
  revisions.set(owner,(revisions.get(owner)||0)+1);
  for(const [k,v] of memory)if(v.owner===owner)forget(k);
  const prefix=PREFIX+encodeURIComponent(owner)+':';
  for(const k of pending.keys())if(k.startsWith(prefix))pending.delete(k);
  try{const s=storage();if(s)Object.keys(s).filter(k=>k.startsWith(prefix)).forEach(k=>s.removeItem(k));}catch{}
}
export function invalidatesResources(endpoint, options = {}) {
  const method=String(options.method || 'GET').toUpperCase();
  if(['GET','HEAD','OPTIONS'].includes(method))return false;
  return /^\/api\/(?:meetings|meeting-recordings|v1\/recordings|recordings|team-recordings|team-recording-types|external-recordings|dictaphone|prompts|dashboard-templates|neo-ai|library|admin\/(?:users|settings))(?:\/|\?|$)/.test(endpoint) &&
    !/^\/api\/meetings\/(?:bbb-history\/sync|availability)(?:\?|$)/.test(endpoint);
}
// BBB discovery is expensive. Share an in-flight lookup and start at most once
// per minute per account; explicit resource reloads and processing polls are unaffected.
export function backgroundRefresh(runtime, name, perform) {
  const owner=resourceOwner(runtime);if(!owner)return Promise.resolve(null);
  const k=owner+':'+name, previous=refreshes.get(k);
  if(previous?.pending)return previous.pending;
  if(previous && Date.now()-previous.at<60000)return Promise.resolve(null);
  const started=generation, revision=revisions.get(owner)||0;
  const entry={at:Date.now(),pending:null};
  const promise=Promise.resolve().then(perform).then(data => started===generation && owner===resourceOwner(runtime) && revision===(revisions.get(owner)||0)?data:null).catch(error=>{if(refreshes.get(k)===entry)refreshes.delete(k);throw error;}).finally(()=>{entry.pending=null;});
  entry.pending=promise;refreshes.set(k,entry);
  while(refreshes.size>32)refreshes.delete(refreshes.keys().next().value);
  return promise;
}
